import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { JobApplication } from "./ApplicationWizard";

// Re-export JobApplication type for convenience
export type { JobApplication };
import { ComprehensiveJobDisplay } from "./ComprehensiveJobDisplay";
import { JobAnalysisDisplay } from "./JobAnalysisDisplay";
import {
  jobApplicationService,
  CompanyIntelligenceResponse,
} from "../services/jobApplicationService";
import { useMessageBox } from "./MessageBox";
import toast from "react-hot-toast";
import "./JobApplicationForm.css";
// Import wizard CSS for modal overlay styles when isModal=true
import "./ApplicationWizard.css";

interface JobApplicationFormProps {
  jobData: JobApplication;
  onSave: (application: JobApplication) => void;
  onClose: () => void;
  onBack?: () => void; // Optional back handler for wizard context
  viewMode?: boolean;
  isProcessing?: boolean;
  isModal?: boolean; // If true, wrap in centered modal overlay with header
  isSlider?: boolean; // If true, wrap in right-side slider overlay with header
  modalTitle?: string; // Title for modal/slider header (default: "Edit Application")
  onOpenCompanyIntelligence?: (
    applicationSnapshot?: Partial<JobApplication>,
  ) => void; // Optional handler to open company intelligence panel
  disableSaveButton?: boolean;
}

export const JobApplicationForm: React.FC<JobApplicationFormProps> = ({
  jobData: initialJobData,
  onSave,
  onClose,
  onBack,
  viewMode = false,
  isProcessing = false,
  isModal = false,
  isSlider = false,
  modalTitle = "Edit Application",
  onOpenCompanyIntelligence,
  disableSaveButton = false,
}) => {
  const [formData, setFormData] =
    useState<Partial<JobApplication>>(initialJobData);
  const [companyIntelligence, setCompanyIntelligence] =
    useState<CompanyIntelligenceResponse | null>(null);
  const [isLoadingCompanyIntelligence, setIsLoadingCompanyIntelligence] =
    useState(false);
  const [companyIntelligenceError, setCompanyIntelligenceError] = useState<
    string | null
  >(null);
  const [isFetchingCompanyIntelligence, setIsFetchingCompanyIntelligence] =
    useState(false);
  const [existingApplication, setExistingApplication] = useState<{
    id: string;
    status: string;
  } | null>(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(false);
  const hasCheckedExistingRef = useRef<string | null>(null); // Track which URL we've already checked
  const [showTooltip, setShowTooltip] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // NOTE: All resume-related functionality has been removed from edit form
  // Resume assignment and fit analysis are now handled exclusively through ResumeAssignmentModal

  const { showMessageBox } = useMessageBox();

  // Update formData when jobData prop changes
  useEffect(() => {
    const urlChanged = initialJobData.url !== formData.url;
    setFormData(initialJobData);
    // Reset the check flag only when URL actually changes (new job being loaded)
    if (urlChanged && initialJobData.url !== hasCheckedExistingRef.current) {
      hasCheckedExistingRef.current = null;
      setExistingApplication(null);
    }
  }, [initialJobData]);

  // Check for existing application by URL (only for new applications, not edit)
  useEffect(() => {
    const checkExistingApplication = async () => {
      // Only check if:
      // 1. Not in view mode (not editing/viewing)
      // 2. URL is available
      // 3. We haven't already checked this URL
      if (viewMode) {
        return; // Don't clear existing state
      }

      if (!formData.url || !formData.url.trim()) {
        return; // Don't clear existing state
      }

      // Skip if we've already checked this URL AND we have an existing application set
      // This prevents re-checking and clearing the banner when formData updates
      if (
        hasCheckedExistingRef.current === formData.url &&
        existingApplication
      ) {
        return;
      }

      // If we've checked but don't have existingApplication, allow re-check (might have been cleared)
      if (
        hasCheckedExistingRef.current === formData.url &&
        !existingApplication
      ) {
        return;
      }

      setIsCheckingExisting(true);
      hasCheckedExistingRef.current = formData.url; // Mark as checked

      try {
        const existing = await jobApplicationService.findApplicationByUrl(
          formData.url,
        );
        if (existing) {
          setExistingApplication(existing);
        } else {
          setExistingApplication(null);
        }
      } catch (error) {
        // Don't show error to user - just proceed without status banner
        setExistingApplication(null);
      } finally {
        setIsCheckingExisting(false);
      }
    };

    checkExistingApplication();
    // Only re-run when URL or viewMode changes, not when id/status changes
    // This preserves the banner state once it's set
  }, [formData.url, viewMode]);

  // Define fetchCompanyIntelligence BEFORE any useEffect that uses it
  const fetchCompanyIntelligence = useCallback(
    async (
      company: string,
      url?: string,
      location?: string,
      showMessageOnError: boolean = true,
    ) => {
      if (!company || company.trim() === "") {
        return;
      }

      setIsLoadingCompanyIntelligence(true);
      setCompanyIntelligenceError(null);

      try {
        const intelligence = await jobApplicationService.getCompanyIntelligence(
          company,
          {
            url,
            city: location,
          },
        );

        // Handle async processing status (prevents 504 timeout)
        if (intelligence.status === "processing") {
          console.log(
            "[DEBUG] Company intelligence is being processed asynchronously",
          );
          setCompanyIntelligenceError(null);
          // Set a placeholder to prevent infinite re-fetching
          // This ensures the useEffect condition (!companyIntelligence) becomes false
          setCompanyIntelligence({ status: "processing" } as any);
          setIsLoadingCompanyIntelligence(false);
          return;
        }

        setCompanyIntelligence(intelligence);
        setFormData((prev) => ({
          ...prev,
          company_intelligence: intelligence,
        }));
      } catch (error) {
        console.error("Error fetching company intelligence:", error);
        // Don't show error for 504 - it's handled by async processing
        if (error instanceof Error && error.message.includes("504")) {
          console.log(
            "[DEBUG] Company intelligence request timed out, will be processed asynchronously",
          );
          setCompanyIntelligenceError(null);
          setCompanyIntelligence({ status: "processing" } as any);
        } else {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to fetch company intelligence";
          setCompanyIntelligenceError(
            errorMessage,
          );
          setCompanyIntelligence(null);
          if (showMessageOnError) {
            showMessageBox({
              type: "error",
              title: "Company intelligence unavailable",
              message: errorMessage,
              buttons: ["ok"],
              onButtonClick: () => {},
            });
          }
        }
      } finally {
        setIsLoadingCompanyIntelligence(false);
      }
    },
    [showMessageBox],
  );

  const openCompanyIntelligencePanel = useCallback(() => {
    if (!onOpenCompanyIntelligence) {
      return false;
    }
    onOpenCompanyIntelligence(formData);
    return true;
  }, [onOpenCompanyIntelligence, formData]);

  const handleOpenCompanyIntelligenceDetails = useCallback(() => {
    if (!onOpenCompanyIntelligence) {
      return;
    }
    onOpenCompanyIntelligence(formData);
  }, [onOpenCompanyIntelligence, formData]);

  // Handler for manual company intelligence fetch/refresh
  const handleFetchCompanyIntelligence = useCallback(async () => {
    if (!formData.company || formData.company.trim() === "") {
      toast.error(
        "Please enter a company name to generate company intelligence.",
        {
          duration: 4000,
          position: "top-right",
        },
      );
      return;
    }

    // If onOpenCompanyIntelligence is provided, open the panel instead
    if (openCompanyIntelligencePanel()) {
      return;
    }

    // Otherwise, fetch inline (existing behavior)
    setIsFetchingCompanyIntelligence(true);
    // Clear existing intelligence to force refresh
    setCompanyIntelligence(null);

    try {
      await fetchCompanyIntelligence(
        formData.company,
        formData.url || undefined,
        formData.location || undefined,
        true, // Show message on error for manual fetch
      );
    } finally {
      setIsFetchingCompanyIntelligence(false);
    }
  }, [
    formData.company,
    formData.url,
    formData.location,
    fetchCompanyIntelligence,
    openCompanyIntelligencePanel,
  ]);

  // Load company intelligence when component mounts or company changes.
  // Tracks the last fetched key to prevent duplicate requests when formData
  // updates but the relevant fields haven't actually changed.
  const lastCompanyFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (formData.company_intelligence && !companyIntelligence) {
      setCompanyIntelligence(formData.company_intelligence);
      return;
    }

    if (isLoadingCompanyIntelligence || companyIntelligence) {
      return;
    }

    if (!formData.company) return;

    const fetchKey = `${formData.company}|${formData.url ?? ""}|${formData.location ?? ""}`;
    if (fetchKey === lastCompanyFetchKeyRef.current) return;
    lastCompanyFetchKeyRef.current = fetchKey;

    fetchCompanyIntelligence(
      formData.company,
      formData.url || undefined,
      formData.location || undefined,
      false,
    );
  }, [
    formData.company,
    formData.url,
    formData.location,
    formData.company_intelligence,
  ]);

  // NOTE: Fit analysis loading removed - handled exclusively by ResumeAssignmentModal

  const handleInputChange = useCallback(
    (field: keyof JobApplication, value: string | string[] | any) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const [isSaving, setIsSaving] = useState(false);
  const isSaveLocked = Boolean(existingApplication) || disableSaveButton;
  const saveButtonLabel = disableSaveButton
    ? "Application Saved"
    : "Save Application";
  const disabledTooltipTitle = disableSaveButton
    ? "Application already saved from extension"
    : "Application already saved";

  const handleSave = useCallback(async () => {
    // Immediately disable button and show loading state
    setIsSaving(true);
    try {
      // Ensure required fields are present
      if (
        !formData.url ||
        !formData.description ||
        !formData.status ||
        !formData.appliedDate
      ) {
        throw new Error(
          "Missing required fields: url, description, status, or appliedDate",
        );
      }
      await onSave(formData as JobApplication);
    } catch (error) {
      console.error("Error saving application:", error);
      throw error;
    } finally {
      // Re-enable save button after completion. The form may stay mounted in extension contexts.
      setIsSaving(false);
    }
  }, [formData, onSave]);

  // Status badge color mapping
  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === "pending") return "#ff9800"; // Orange
    if (statusLower === "applied") return "#2196f3"; // Blue
    if (statusLower === "interviewing") return "#9c27b0"; // Purple
    if (statusLower === "offer") return "#4caf50"; // Green
    if (statusLower === "rejected") return "#f44336"; // Red
    return "#757575"; // Gray for unknown
  };

  const formContent = (
    <div className="job-application-form">
      {/* Debug: Show checking state */}
      {isCheckingExisting && (
        <div className="debug-banner-checking">
          🔍 Checking for existing application...
        </div>
      )}

      <div className="job-application-form-content">
        <ComprehensiveJobDisplay
          jobData={formData}
          onDataChange={handleInputChange}
          isProcessing={isProcessing}
          viewMode={viewMode}
          companyIntelligence={companyIntelligence}
          isLoadingCompanyIntelligence={isLoadingCompanyIntelligence}
          companyIntelligenceError={companyIntelligenceError}
          onFetchCompanyIntelligence={handleFetchCompanyIntelligence}
          isFetchingCompanyIntelligence={isFetchingCompanyIntelligence}
          onOpenCompanyIntelligenceDetails={
            onOpenCompanyIntelligence
              ? handleOpenCompanyIntelligenceDetails
              : undefined
          }
          hideResumeSelection={true} // All resume functionality removed from edit form - use ResumeAssignmentModal instead
        />

        {/* Show JobAnalysisDisplay when fitScore exists */}
        {formData.fitScore && (
          <JobAnalysisDisplay jobData={formData} isProcessing={isProcessing} />
        )}
      </div>

      <div className="job-application-form-footer">
        <div className="job-application-form-buttons">
          {viewMode ? (
            <button className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          ) : (
            <>
              {/* Status banner for previously saved application - before Cancel button */}
              {existingApplication && !viewMode && (
                <div
                  className="previously-saved-status-banner"
                  style={
                    {
                      "--status-color": getStatusColor(
                        existingApplication.status,
                      ),
                      borderColor: `${getStatusColor(existingApplication.status)}40`,
                      borderLeftColor: getStatusColor(
                        existingApplication.status,
                      ),
                    } as React.CSSProperties
                  }
                >
                  {/* Status indicator dot */}
                  <div className="status-dot" />

                  {/* Content */}
                  <span className="previously-saved-status-content">
                    <span className="status-label">Previously saved:</span>
                    <span className="status-value">
                      {existingApplication.status}
                    </span>
                  </span>
                </div>
              )}

              {/* Temporary debug banner - shows checking state */}
              {isCheckingExisting && !existingApplication && (
                <div className="debug-banner-checking-small">
                  🔍 Checking...
                </div>
              )}

              <div
                className={`job-application-form-actions${onBack ? " job-application-form-actions--with-back" : ""}`}
              >
                {onBack && (
                  <button className="btn btn-secondary" onClick={onBack}>
                    Back
                  </button>
                )}
                <button className="btn btn-secondary" onClick={onClose}>
                  Cancel
                </button>

                {/* Save button with tooltip when disabled due to existing application */}
                {isSaveLocked ? (
                  <div
                    className="job-application-form-save-tooltip"
                    onMouseEnter={() => {
                      setShowTooltip(true);
                    }}
                    onMouseLeave={() => {
                      setShowTooltip(false);
                    }}
                  >
                    <button
                      className={`btn btn-primary btn-disabled job-application-form-save-button ${isSaving ? "btn-saving" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      disabled={true}
                    >
                      {isSaving ? (
                        <>
                          <div className="button-spinner-small"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        saveButtonLabel
                      )}
                    </button>

                    {/* Simple tooltip */}
                    {showTooltip && (
                      <div className="tooltip-container">
                        <div className="existing-application-banner">
                          <span>{disabledTooltipTitle}</span>
                          {existingApplication && (
                            <span className="existing-application-status">
                              (Status:{" "}
                              <strong className="existing-application-status-strong">
                                {existingApplication.status}
                              </strong>
                              )
                            </span>
                          )}
                        </div>
                        <div className="existing-application-hint">
                          {disableSaveButton
                            ? "This job was already saved successfully in the extension."
                            : "Use edit from your applications list to update."}
                        </div>
                        {/* Tooltip arrow */}
                        <div className="tooltip-arrow" />
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    className={`btn btn-primary job-application-form-save-button ${isSaving ? "btn-saving" : ""}`}
                    onClick={handleSave}
                    disabled={
                      isSaving ||
                      !formData.url?.trim() ||
                      !formData.description?.trim()
                    }
                  >
                    {isSaving ? (
                      <>
                        <div className="button-spinner-small"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      "Save Application"
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Handle escape key and prevent body scroll for slider mode
  useEffect(() => {
    if (!isSlider) return;

    const shouldLockBodyScroll =
      typeof window !== "undefined" && window.innerWidth <= 768;
    const previousOverflow = document.body.style.overflow;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    if (shouldLockBodyScroll) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isSlider, onClose]);

  // Only render portal after component is mounted (client-side only)
  // This prevents React error #310 (hydration mismatch)
  if (
    isSlider &&
    (!isMounted || typeof document === "undefined" || !document.body)
  ) {
    return null;
  }

  // If slider mode, wrap in right-side slider overlay
  if (isSlider) {
    return createPortal(
      <div
        className="edit-application-slider-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          className="edit-application-slider"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="edit-application-slider-header">
            <div className="edit-application-slider-title">
              <h2>{modalTitle}</h2>
            </div>
            <button
              className="edit-application-slider-close"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="edit-application-slider-content">{formContent}</div>
        </div>
      </div>,
      document.body,
    );
  }

  // If modal mode, wrap in overlay and modal container
  if (isModal) {
    return (
      <div className="wizard-overlay">
        <div className="wizard-modal wizard-modal-opened">
          <div className="wizard-header">
            <h2 className="wizard-title">{modalTitle}</h2>
            <button className="wizard-close" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="wizard-content p-0">{formContent}</div>
        </div>
      </div>
    );
  }

  // Otherwise return form directly (for wizard or extension use)
  return formContent;
};
